/**
 * Caja "Facturación / albarán" en la ficha de reparación.
 * Permite generar factura/albarán a partir de las líneas de productos y
 * lista los documentos ya creados. Extraído de `RepairForm.jsx`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtPrice } from "@/lib/totals";
import {
  Receipt, Truck, AlertTriangle, ExternalLink, Check,
} from "lucide-react";
import Box from "@/components/repairs/Box";
import { Dialog } from "@/components/repairs/RepairFormAtoms";

export default function BoxDocuments({ repair, isNew, repairId, onUpdated }) {
  const [docs, setDocs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);
  // Tras crear correctamente factura/albarán, preguntar si marcar como "Entregado".
  const [deliverPrompt, setDeliverPrompt] = useState(false);
  const [delivering, setDelivering] = useState(false);

  const load = async () => {
    if (isNew || !repairId) return;
    try {
      const r = await api.get(`/repairs/${repairId}/documents`);
      setDocs(r.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [repairId, repair?.items?.length]);

  const hasItems = (repair?.items || []).length > 0;
  const hasCustomer = !!repair?.customer_id;

  const create = async (kind, force = false) => {
    if (!hasCustomer) {
      toast.error("Debes seleccionar un cliente antes de crear una factura o albarán.");
      return;
    }
    if (!hasItems) {
      toast.error("Debes añadir al menos un producto o servicio antes de crear una factura o albarán.");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post(`/repairs/${repairId}/${kind === "invoice" ? "invoice" : "delivery-note"}${force ? "?force=true" : ""}`);
      toast.success(`${kind === "invoice" ? "Factura" : "Albarán"} ${r.data.code} creado`);
      setConfirmKind(null);
      load();
      onUpdated && onUpdated();
      // Solo tras crear correctamente y si la reparación no está ya entregada.
      if ((repair?.status || "") !== "entregado") setDeliverPrompt(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.message) {
        setConfirmKind(kind);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setCreating(false);
    }
  };

  // Cambia el estado a "entregado" usando el flujo estándar (PUT /repairs/{id}),
  // que registra el cambio en el timeline con usuario y fecha/hora.
  const markDelivered = async () => {
    setDelivering(true);
    try {
      await api.put(`/repairs/${repairId}`, { status: "entregado" });
      toast.success("Reparación marcada como entregada");
      setDeliverPrompt(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDelivering(false);
    }
  };

  return (
    <Box title="Facturación / albarán" testid="repair-box-documents">
      {isNew ? (
        <div className="text-xs text-amber-700">
          Guarda primero la reparación.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => create("invoice")}
              disabled={creating || !hasItems || !hasCustomer}
              data-testid="repair-create-invoice"
              className="inline-flex flex-col items-center justify-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-3 text-xs font-semibold disabled:opacity-50"
            >
              <Receipt size={16} />
              Crear factura
            </button>
            <button
              type="button"
              onClick={() => create("delivery_note")}
              disabled={creating || !hasItems || !hasCustomer}
              data-testid="repair-create-delivery"
              className="inline-flex flex-col items-center justify-center gap-1 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-3 text-xs font-semibold disabled:opacity-50"
            >
              <Truck size={16} />
              Crear albarán
            </button>
          </div>

          {!hasCustomer && (
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1">
              Falta cliente.
            </div>
          )}
          {!hasItems && (
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1">
              Sin líneas en el recuadro 4.
            </div>
          )}

          <div className="pt-2 border-t border-[#E5E5E0]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
              Documentos generados ({docs.length})
            </div>
            {docs.length === 0 ? (
              <div className="text-xs text-[#525252]">Sin documentos.</div>
            ) : (
              <div className="space-y-1.5">
                {docs.map((d) => (
                  <div
                    key={d.id}
                    className="border border-[#E5E5E0] p-2 bg-white hover:border-[#6E8B3D] transition-colors"
                    data-testid={`repair-doc-${d.code}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-mono uppercase border ${d.kind === "invoice" ? "bg-[#6E8B3D] text-white border-[#556C2E]" : "bg-neutral-100 text-neutral-700 border-neutral-300"}`}>
                            {d.kind === "invoice" ? "Factura" : "Albarán"}
                          </span>
                          <span className="font-mono text-xs font-semibold">{d.code}</span>
                        </div>
                        <div className="text-[10px] text-[#525252] font-mono mt-0.5">
                          {new Date(d.created_at).toLocaleDateString("es")} · {fmtPrice(d.total)}
                        </div>
                      </div>
                      <Link
                        to={`/billing?doc=${d.id}`}
                        data-testid={`repair-doc-open-${d.code}`}
                        title={`Abrir ${d.kind === "invoice" ? "factura" : "albarán"} ${d.code}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-[#111110] hover:bg-[#111110] hover:text-white transition-colors shrink-0"
                      >
                        Abrir <ExternalLink size={11} />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {confirmKind && (
        <Dialog onClose={() => setConfirmKind(null)} title="Documento ya existe">
          <p className="text-sm">
            Ya existe un documento generado para esta reparación. ¿Quieres crear otro?
          </p>
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmKind(null)}
              className="border border-[#111110] hover:bg-[#F7F7F4] px-3 py-2 text-sm flex-1"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => create(confirmKind, true)}
              data-testid="repair-doc-force"
              className="bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold flex-1 inline-flex items-center justify-center gap-1"
            >
              <AlertTriangle size={14} /> Crear otro
            </button>
          </div>
        </Dialog>
      )}

      {deliverPrompt && (
        <Dialog onClose={() => setDeliverPrompt(false)} title="¿Marcar como entregado?">
          <div className="space-y-1" data-testid="repair-deliver-prompt">
            {repair?.code && (
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Reparación {repair.code}</p>
            )}
            <p className="text-sm">
              ¿Deseas cambiar la sección de esta reparación a <strong>“Entregado”</strong>?
            </p>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDeliverPrompt(false)}
              data-testid="repair-deliver-no"
              className="border border-[#111110] hover:bg-[#F7F7F4] px-3 py-2 text-sm flex-1"
            >
              No, mantener sección
            </button>
            <button
              type="button"
              onClick={markDelivered}
              disabled={delivering}
              data-testid="repair-deliver-yes"
              className="bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold flex-1 inline-flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <Check size={14} /> Sí, marcar como entregado
            </button>
          </div>
        </Dialog>
      )}
    </Box>
  );
}
